import { useSuspenseQuery, type QueryClient } from '@tanstack/react-query'

import {
  MasterOnboardingForm,
  type DeskVariant,
} from '#admin/features/exhibitor/onboarding/master-onboarding-form.tsx'
import {
  applicationsQuery,
  badgeTypesQuery,
  boothsQuery,
  boothTypesQuery,
  catalogQuery,
  companiesQuery,
} from '#admin/features/exhibitor/queries.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'

/** What the stand desk reads. A route loader calls it, so the page does not wait on each query. */
export function prefetchExhibitorDesk(queryClient: QueryClient) {
  return Promise.all([
    queryClient.ensureQueryData(companiesQuery()),
    queryClient.ensureQueryData(applicationsQuery()),
    queryClient.ensureQueryData(boothsQuery()),
    queryClient.ensureQueryData(boothTypesQuery()),
    queryClient.ensureQueryData(catalogQuery()),
    queryClient.ensureQueryData(badgeTypesQuery()),
    queryClient.ensureQueryData(manifestQuery()),
  ])
}

/**
 * The stand desk with its data: `/exhibition/exhibitors/new`, and the exhibitor and sponsor
 * choices of the registration desk.
 */
export function ExhibitorDesk({ variant = 'exhibition' }: { variant?: DeskVariant }) {
  const { data: companies } = useSuspenseQuery(companiesQuery())
  const { data: applications } = useSuspenseQuery(applicationsQuery())
  const { data: booths } = useSuspenseQuery(boothsQuery())
  const { data: boothTypes } = useSuspenseQuery(boothTypesQuery())
  const { data: catalog } = useSuspenseQuery(catalogQuery())
  const { data: badgeTypes } = useSuspenseQuery(badgeTypesQuery())
  const { data: manifest } = useSuspenseQuery(manifestQuery())

  // A badge needs `ticket`. With it read-only, the server refuses to issue one.
  const canIssueBadges = manifest?.modules['ticket'] === 'full'

  return (
    <MasterOnboardingForm
      companies={companies.items}
      applications={applications.items}
      booths={booths.items}
      boothTypes={boothTypes.items}
      catalog={catalog.items}
      badgeTypes={canIssueBadges ? badgeTypes.items : []}
      editionCurrency={manifest?.tenant.currencyCode ?? 'IDR'}
      eventName={manifest?.tenant.name ?? ''}
      variant={variant}
    />
  )
}
